/**
 * Redaction of credentials from everything the services and frontends hand to telemetry and logs: request
 * headers, URLs, free text, Application Insights telemetry items, and arbitrary values such as the arguments
 * of a traced call. Headers pass an allowlist, so an unknown header never reaches a sink with its value;
 * every other shape is scanned for the places a credential travels — sensitive query parameters and object
 * keys, authorization schemes, JSON Web Tokens, URL user info — and loses its value there.
 */
/** Stands in for every value that is withheld. */
const REDACTED = '[REDACTED]';
/** Headers that never carry a credential; every other header keeps its name but loses its value. */
const SAFE_HEADER_NAMES = new Set([
    'accept',
    'accept-encoding',
    'accept-language',
    'cache-control',
    'content-encoding',
    'content-language',
    'content-length',
    'content-type',
    'host',
    'origin',
    'referer',
    'request-id',
    'traceparent',
    'tracestate',
    'user-agent',
    'x-correlation-id',
    'x-ms-request-id',
    'x-request-id',
]);
/** Safe headers whose value is a URL: kept without query and fragment, which may hold a token. */
const URL_HEADER_NAMES = new Set(['origin', 'referer']);
/**
 * Query and fragment parameters whose value is withheld: tokens, keys, signatures (`sig` of a SAS), codes
 * (an OAuth code, an Azure Functions key), sessions, and tRPC's `input`, which carries a procedure's whole
 * input — the selfservice token among it. Keys, signatures and codes match by the whole name or its ending,
 * so a diagnostic value such as `statusCode=404`, `errorCode=BlobNotFound` or `keyCount=3` stays readable.
 */
const SENSITIVE_PARAMETER_PATTERN = /token|secret|passw|pwd|auth(?!or)|keys?$|^sig$|signature|^code$|device[-_]?code|session|cookie|credential|assertion|^input$/i;
/**
 * Object keys and parameter names that spell a credential (`authorization`, `cookie`, `password`, `apiKey`,
 * `accessToken`, …); the value under one is withheld. Numbers and booleans under such a name stay, so a
 * count like `inputTokens` or an amount like `totalAuthorizationEur` keeps its diagnostic value.
 */
const SENSITIVE_KEY_PATTERN = /authorization|cookie|passw|^pwd$|^pass$|secret|token|api[-_]?key|(subscription|functions|account|access|private|master)[-_]?key|^key$|credential|connection[-_]?string|signature|^sig$|assertion/i;
/** Keys of a headers object, which is passed through the header allowlist as a whole. */
const HEADERS_KEY_PATTERN = /headers/i;
/** A `name=value` pair after a URL's `?`, `&`, `#` or `;`, or a `name=value` pair in text such as a connection string. */
const PARAMETER_PATTERN = /(^|[?&#;\s])([^=&#;?\s"'<>]+)=([^&#;\s"'<>]*)/g;
/**
 * The user info of an absolute URL (`https://user:password@host`). The scheme is bounded to 64 characters, longer
 * than any real scheme, so that a long run of scheme characters without `://` (a crafted path like `/a.a.a…`) costs
 * linear rather than quadratic time.
 */
const URL_USER_INFO_PATTERN = /\b([a-z][a-z0-9+.-]{0,63}:\/\/)[^/?#@\s"'<>]*@/gi;
/** An HTTP authorization scheme with its credential (`Bearer eyJ…`, `Basic dXNlcjpwYXNz`, `SharedKey acc:sig`). */
const AUTHORIZATION_SCHEME_PATTERN = /\b(Bearer|Basic|Digest|SharedKey|SharedAccessSignature|Negotiate|NTLM)\s+([^\s"',;]+)/gi;
/**
 * Whether the word after an authorization scheme looks like a credential rather than prose: it holds a digit,
 * a symbol of base64 or a key pair, or a capital past its first letter, or it is longer than any word. So
 * `Basic validation failed` keeps its text while `Basic dXNlcjpwYXNz` does not.
 */
function looksLikeCredential(word) {
    // trailing punctuation belongs to the sentence
    const core = word.replace(/[.:!?)\]]+$/, '');
    return /[0-9+/=_.~:-]/.test(core) || /[A-Z]/.test(core.slice(1)) || core.length >= 24;
}
/**
 * A JSON Web Token: two base64url segments starting with `{"` (`eyJ`), a signature segment optional. The token must
 * start a run of token characters (group 1 keeps the character before it), so that a long run like `eyJ-eyJ-…`
 * without a dot is scanned once, in linear rather than quadratic time. No lookbehind: the module also runs in browsers.
 */
const JWT_PATTERN = /(^|[^A-Za-z0-9_-])eyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}(?:\.[A-Za-z0-9_-]*)?/g;
/** A JSON string property, as an error message embeds a serialized request or response body. */
const JSON_STRING_PROPERTY_PATTERN = /"([^"\\]+)"(\s*:\s*)"(?:[^"\\]|\\.)*"/g;
/** How many array items or object keys a redacted value keeps; the rest is summarized. */
const MAX_COLLECTION_ENTRIES = 100;
/**
 * The headers of a request or response as a plain record that holds no credential: an allowlisted header
 * keeps its value (a URL-valued one without query), every other header keeps its name with the value
 * withheld. Accepts every shape headers come in — a plain record, a Fetch `Headers`, a `Map`, entry pairs,
 * Node's flat `rawHeaders` list, an iterable of entries.
 */
function redactHeaders(headers) {
    // each header passes the allowlist once, under its lower-case name
    const redacted = {};
    for (const [name, value] of listHeaderEntries(headers)) {
        const lowerName = name.toLowerCase();
        redacted[lowerName] = SAFE_HEADER_NAMES.has(lowerName) ? redactSafeHeaderValue(lowerName, value) : REDACTED;
    }
    return redacted;
}
/** The value of an allowlisted header; a URL-valued one is cut before its query and fragment. */
function redactSafeHeaderValue(lowerName, value) {
    // a repeated header arrives as a list
    const text = Array.isArray(value) ? value.map(String).join(', ') : String(value);
    if (!URL_HEADER_NAMES.has(lowerName)) {
        return text;
    }
    return redactText(text.split(/[?#]/, 1)[0]);
}
/** The `[name, value]` pairs of headers in any of the shapes they come in; anything else holds none. */
function listHeaderEntries(headers) {
    // nothing to list in a primitive
    if (typeof headers !== 'object' || headers === null) {
        return [];
    }
    // entry pairs (`[['accept', '*/*']]`) or Node's flat `rawHeaders` list (`['Accept', '*/*']`)
    if (Array.isArray(headers)) {
        if (headers.every((entry) => Array.isArray(entry) && entry.length === 2)) {
            return headers.map(([name, value]) => [String(name), value]);
        }
        const pairs = [];
        for (let index = 0; index + 1 < headers.length; index += 2) {
            pairs.push([String(headers[index]), headers[index + 1]]);
        }
        return pairs;
    }
    // a Fetch `Headers` or a `Map`, both of which call back with the value first
    if ('forEach' in headers && typeof headers.forEach === 'function') {
        const pairs = [];
        headers.forEach((value, name) => pairs.push([String(name), value]));
        return pairs;
    }
    // any other iterable of entries, such as the Azure SDK's `HttpHeaders`
    if (Symbol.iterator in headers && typeof headers[Symbol.iterator] === 'function') {
        return [...headers]
            .filter((entry) => Array.isArray(entry) && entry.length === 2)
            .map(([name, value]) => [String(name), value]);
    }
    // a plain record, as Node and Express hold incoming headers
    return Object.entries(headers);
}
/**
 * The URL with every credential it can carry withheld: the user info before the host and the value of each
 * sensitive query or fragment parameter. Works on absolute and relative URLs alike and leaves the path,
 * which the request needs to be recognized, untouched.
 */
export function redactUrl(url) {
    // the user info is a password in all but name
    const withoutUserInfo = url.replace(URL_USER_INFO_PATTERN, `$1${REDACTED}@`);
    // only the part from the first `?` or `#` on holds parameters
    const parametersStart = withoutUserInfo.search(/[?#]/);
    if (parametersStart === -1) {
        return withoutUserInfo;
    }
    return withoutUserInfo.slice(0, parametersStart) + redactParameters(withoutUserInfo.slice(parametersStart));
}
/** The text with the value of every sensitive `name=value` parameter withheld. */
function redactParameters(text) {
    return text.replace(PARAMETER_PATTERN, (pair, separator, name) => SENSITIVE_PARAMETER_PATTERN.test(decodeParameterName(name)) ? `${separator}${name}=${REDACTED}` : pair);
}
/** The decoded parameter name (`access%5Ftoken` → `access_token`); a malformed escape is matched as written. */
function decodeParameterName(name) {
    // a malformed escape cannot hide a sensitive name behind it any better than the raw spelling does
    try {
        return decodeURIComponent(name);
    }
    catch {
        return name;
    }
}
/**
 * Free text — an error message, a stack, a log line, a serialized object — with every credential it may
 * embed withheld: URL user info, sensitive `name=value` parameters (in URLs and connection strings alike),
 * HTTP authorization schemes, JSON Web Tokens, and sensitive JSON string properties.
 */
export function redactText(text) {
    // user info and parameters first, as in a URL; then the credentials that stand on their own
    const withoutParameters = redactParameters(text.replace(URL_USER_INFO_PATTERN, `$1${REDACTED}@`));
    return withoutParameters
        .replace(AUTHORIZATION_SCHEME_PATTERN, (match, scheme, credential) => looksLikeCredential(credential) ? `${scheme} ${REDACTED}` : match)
        .replace(JWT_PATTERN, `$1${REDACTED}`)
        .replace(JSON_STRING_PROPERTY_PATTERN, (property, key, colon) => isSensitiveKey(key) ? `"${key}"${colon}"${REDACTED}"` : property);
}
/**
 * A copy of any value that holds no credential, for a sink that serializes or inspects it: headers pass the
 * allowlist, an HTTP request or response shrinks to its method, redacted URL and headers or its status, a
 * sensitive key loses its string value, and every string is redacted as text. Objects are copied as plain
 * records of their own enumerable keys — a getter on a prototype could hand the credential back — down to
 * `maxDepth` levels, below which a nested object becomes a placeholder; a circular reference does too.
 */
export function redactValue(value, maxDepth) {
    return redactNested(value, 0, maxDepth, new WeakSet());
}
/** One level of `redactValue`; `ancestors` holds the objects on the path down, to break cycles. */
function redactNested(value, depth, maxDepth, ancestors) {
    // strings may embed a credential; other primitives cannot
    if (typeof value === 'string') {
        return redactText(value);
    }
    if (typeof value !== 'object' || value === null) {
        return value;
    }
    // values whose printed form holds no nested data stay as they are
    if (value instanceof Date || value instanceof RegExp) {
        return value;
    }
    // binary data (a request or response body as a `Buffer`) may encode a credential no text pattern can
    // find, so only its size is kept
    if (ArrayBuffer.isView(value) || value instanceof ArrayBuffer) {
        return `[Binary: ${value.byteLength} bytes]`;
    }
    if (value instanceof URL) {
        return redactUrl(value.href);
    }
    if (value instanceof Error) {
        return `${value.name}: ${redactText(value.message)}`;
    }
    // stop at the depth limit and at a cycle
    if (depth >= maxDepth) {
        return Array.isArray(value) ? '[Array]' : '[Object]';
    }
    if (ancestors.has(value)) {
        return '[Circular]';
    }
    ancestors.add(value);
    const redacted = redactObject(value, depth, maxDepth, ancestors);
    ancestors.delete(value);
    return redacted;
}
/** The redacted copy of an object below the depth limit, by the kind of object it is. */
function redactObject(value, depth, maxDepth, ancestors) {
    // an HTTP request (Node's, Express's, Fetch's) shrinks to what identifies it
    if (isHttpRequest(value)) {
        const url = 'originalUrl' in value && typeof value.originalUrl === 'string' ? value.originalUrl : value.url;
        return { method: value.method, url: redactUrl(url), headers: redactHeaders(value.headers) };
    }
    // a server response to its status, a promise to a placeholder
    if (isServerResponse(value)) {
        return { statusCode: value.statusCode };
    }
    if (value instanceof Promise) {
        return '[Promise]';
    }
    // collections item by item, with the item count bounded
    if (Array.isArray(value) || value instanceof Set) {
        const items = [...value];
        const kept = items
            .slice(0, MAX_COLLECTION_ENTRIES)
            .map((item) => redactNested(item, depth + 1, maxDepth, ancestors));
        return items.length > MAX_COLLECTION_ENTRIES
            ? [...kept, `… ${items.length - MAX_COLLECTION_ENTRIES} more`]
            : kept;
    }
    // records and maps key by key, so the key decides about its value
    const entries = value instanceof Map ? [...value.entries()].map(([key, item]) => [String(key), item]) : Object.entries(value);
    const redacted = {};
    for (const [key, item] of entries.slice(0, MAX_COLLECTION_ENTRIES)) {
        redacted[key] = redactEntry(key, item, depth, maxDepth, ancestors);
    }
    if (entries.length > MAX_COLLECTION_ENTRIES) {
        redacted['…'] = `${entries.length - MAX_COLLECTION_ENTRIES} more`;
    }
    return redacted;
}
/**
 * The redacted copy of a value that travels under a name — an object key, or the parameter name of a
 * traced call's argument: under a headers name it passes the header allowlist, under a credential's name it
 * is withheld unless it is a number, a boolean or absent, and otherwise it is redacted as by `redactValue`.
 */
export function redactNamedValue(name, value, maxDepth) {
    // `redactEntry` redacts a value one level below its key; a named value has no enclosing object
    return redactEntry(name, value, -1, maxDepth, new WeakSet());
}
/** The redacted value under a key: headers by allowlist, a sensitive key without its non-numeric value. */
function redactEntry(key, value, depth, maxDepth, ancestors) {
    // a headers object as a whole, whatever shape it has
    if (HEADERS_KEY_PATTERN.test(key) && typeof value === 'object' && value !== null) {
        return redactHeaders(value);
    }
    // a credential under its name; a count or a flag under such a name is no credential
    if (isSensitiveKey(key) && !isHarmlessScalar(value)) {
        return REDACTED;
    }
    return redactNested(value, depth + 1, maxDepth, ancestors);
}
/** Whether the key or parameter name spells a credential. */
function isSensitiveKey(key) {
    return SENSITIVE_KEY_PATTERN.test(key);
}
/** Numbers, booleans and absent values, which cannot be a credential. */
function isHarmlessScalar(value) {
    return value === null || value === undefined || typeof value === 'number' || typeof value === 'boolean';
}
/** Whether the object is an HTTP request: a method, a URL and headers. */
function isHttpRequest(value) {
    return ('method' in value &&
        typeof value.method === 'string' &&
        'url' in value &&
        typeof value.url === 'string' &&
        'headers' in value);
}
/** Whether the object is Node's server response: a status code and a header setter. */
function isServerResponse(value) {
    return ('statusCode' in value &&
        typeof value.statusCode === 'number' &&
        'setHeader' in value &&
        typeof value.setHeader === 'function');
}
/** The fields of an Application Insights telemetry item that can carry a URL or free text. */
const TELEMETRY_TEXT_FIELDS = ['url', 'uri', 'refUri', 'name', 'data', 'target', 'message'];
/** The fields of a parsed stack frame that can carry a URL: the frame line, its function and its file. */
const STACK_FRAME_TEXT_FIELDS = ['assembly', 'method', 'fileName'];
/**
 * Redacts an Application Insights telemetry item in place before it is sent: its URL and text fields, the
 * message and stack of each exception, every string property and every string tag. A path segment that
 * follows one of `secretPathPrefixes` is withheld as well, for a frontend whose routes carry a token in the
 * path (`/status/<token>`). Works on the base data of the Node and the browser SDK alike, which share the
 * Application Insights data contracts.
 */
export function redactTelemetryItem(tags, baseData, secretPathPrefixes) {
    // every text that leaves through this item passes the same redaction, the tags first: the Node SDK keeps
    // them in a record, the browser SDK in an array with named entries and pushed `{ name: value }` objects
    const redact = (text) => redactSecretPathSegments(redactText(text), secretPathPrefixes);
    redactOwnStrings(tags, redact);
    if (Array.isArray(tags)) {
        for (const entry of tags) {
            redactOwnStrings(entry, redact);
        }
    }
    if (!isRecord(baseData)) {
        return;
    }
    // the URL and text fields, and the properties a caller attached
    redactStringFields(baseData, TELEMETRY_TEXT_FIELDS, redact);
    if (isRecord(baseData.properties)) {
        redactStringFields(baseData.properties, Object.keys(baseData.properties), redact);
    }
    // the exceptions of an exception item, each with its message and stack, and the frames of its parsed
    // stack, whose file name is the URL of a script or page
    if (Array.isArray(baseData.exceptions)) {
        for (const exception of baseData.exceptions) {
            if (!isRecord(exception)) {
                continue;
            }
            redactStringFields(exception, ['message', 'stack'], redact);
            if (Array.isArray(exception.parsedStack)) {
                for (const frame of exception.parsedStack) {
                    if (isRecord(frame)) {
                        redactStringFields(frame, STACK_FRAME_TEXT_FIELDS, redact);
                    }
                }
            }
        }
    }
}
/**
 * Redacts a telemetry item of the browser SDK in place, as a telemetry initializer sees it: its tags and base
 * data, the operation name under `ext.trace.name`, which the SDK takes from the page path and the channel
 * sends as the `ai.operation.name` tag only after the initializers ran, and the custom properties under
 * `data`, which the channel sends as the item's properties.
 */
export function redactBrowserTelemetryItem(item, secretPathPrefixes) {
    // an item that is no object carries nothing to redact
    if (!isRecord(item)) {
        return;
    }
    // the tags and the base data, as on an item of the Node SDK
    redactTelemetryItem(item.tags, item.baseData, secretPathPrefixes);
    // the operation name and the custom properties, with the same redaction
    const redact = (text) => redactSecretPathSegments(redactText(text), secretPathPrefixes);
    if (isRecord(item.ext) && isRecord(item.ext.trace)) {
        redactStringFields(item.ext.trace, ['name'], redact);
    }
    redactOwnStrings(item.data, redact);
}
/** Replaces every string under an own key of an object or array with its redaction; other values stay. */
function redactOwnStrings(value, redact) {
    // a primitive has no keys; an array's indices and named entries are keys alike
    if (typeof value !== 'object' || value === null) {
        return;
    }
    for (const [key, item] of Object.entries(value)) {
        if (typeof item === 'string') {
            Object.assign(value, { [key]: redact(item) });
        }
    }
}
/** Replaces each named string field of the record with its redaction; any other field has nothing to redact. */
function redactStringFields(record, fields, redact) {
    for (const field of fields) {
        const value = record[field];
        if (typeof value === 'string') {
            record[field] = redact(value);
        }
    }
}
/**
 * The text with the path segment after each of the prefixes withheld (`/status/ABC` → `/status/[REDACTED]`);
 * the prefix matches in any letter case, as a browser keeps the path as typed (`/Status/ABC`).
 */
function redactSecretPathSegments(text, secretPathPrefixes) {
    return secretPathPrefixes.reduce((redacted, prefix) => redacted.replace(new RegExp(`(${escapeRegExp(prefix)})[^/?#\\s"'<>]+`, 'gi'), `$1${REDACTED}`), text);
}
/** The text with every regular expression metacharacter escaped. */
function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/** Whether the value is a non-null, non-array object. */
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
